import { useCallback, useEffect, useState } from 'react'
import { Eye, FolderTree, Plus, Pencil, Trash2 } from 'lucide-react'
import { PageContainer, PageHeader } from '../../components/Layout'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmModal,
  DataTable,
  EmptyState,
  Input,
  Modal,
  Pagination,
  Select,
  Spinner,
  Textarea,
} from '../../components/UI'
import { entrepreneurCatalogService } from '../../services/entrepreneurCatalogService'

const pageSize = 10

const emptyCategory = {
  name: '',
  slug: '',
  description: '',
  sortOrder: 0,
  isActive: true,
}

const cloneCategory = (category) => ({
  id: category?.id,
  name: category?.name || '',
  slug: category?.slug || '',
  description: category?.description || '',
  sortOrder: Number(category?.sortOrder || 0),
  isActive: category?.isActive ?? true,
})

const readCategoryItems = (response) => {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.items)) return response.items
  if (Array.isArray(response?.data?.items)) return response.data.items
  if (Array.isArray(response?.data)) return response.data
  return []
}

const readPagination = (response, fallbackPage) => {
  if (response && typeof response === 'object' && !Array.isArray(response)) {
    return {
      page: Number(response.page ?? response.currentPage ?? fallbackPage ?? 1),
      pageSize: Number(response.pageSize ?? response.limit ?? pageSize),
      totalCount: Number(response.totalCount ?? response.totalItems ?? readCategoryItems(response).length),
      totalPages: Number(response.totalPages ?? response.pages ?? 1),
    }
  }

  return {
    page: fallbackPage ?? 1,
    pageSize,
    totalCount: readCategoryItems(response).length,
    totalPages: 1,
  }
}

export default function EntrepreneurCatalogCategories() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [profile, setProfile] = useState(null)
  const [categories, setCategories] = useState([])
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize,
    totalCount: 0,
    totalPages: 1,
  })
  const [notice, setNotice] = useState(null)
  const [formOpen, setFormOpen] = useState(false)
  const [formMode, setFormMode] = useState('create')
  const [formCategory, setFormCategory] = useState(emptyCategory)
  const [viewCategory, setViewCategory] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const loadData = useCallback(async (requestedPage = 1, options = {}) => {
    const { clearNotice = true } = options
    setLoading(true)
    if (clearNotice) {
      setNotice(null)
    }

    try {
      const [profileResult, categoriesResult] = await Promise.allSettled([
        entrepreneurCatalogService.getProfile(),
        entrepreneurCatalogService.getCategories({
          page: requestedPage,
          pageSize,
        }),
      ])

      if (profileResult.status === 'fulfilled') {
        setProfile(profileResult.value)
      }

      if (categoriesResult.status === 'fulfilled') {
        const items = readCategoryItems(categoriesResult.value)
        const paging = readPagination(categoriesResult.value, requestedPage)
        setCategories(items)
        setPagination(paging)
      } else {
        throw categoriesResult.reason
      }
    } catch (error) {
      if (error?.response?.status === 402) {
        setNotice({ type: 'error', text: 'Este modulo requiere una licencia activa.' })
      } else {
        setNotice({ type: 'error', text: error?.message || 'No fue posible cargar las categorias.' })
      }
      setCategories([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData(1)
  }, [loadData])

  const openCreateModal = () => {
    setFormMode('create')
    setFormCategory(emptyCategory)
    setFormOpen(true)
  }

  const openEditModal = (category) => {
    setFormMode('edit')
    setFormCategory(cloneCategory(category))
    setFormOpen(true)
  }

  const submitCategory = async () => {
    setSaving(true)
    setNotice(null)

    const payload = {
      name: formCategory.name.trim(),
      slug: formCategory.slug.trim(),
      description: formCategory.description.trim(),
      sortOrder: Number(formCategory.sortOrder || 0),
      isActive: Boolean(formCategory.isActive),
    }

    try {
      if (formMode === 'edit' && formCategory.id) {
        await entrepreneurCatalogService.updateCategory(formCategory.id, payload)
        setNotice({ type: 'success', text: `Categoria "${payload.name}" guardada.` })
      } else {
        await entrepreneurCatalogService.createCategory(payload)
        setNotice({ type: 'success', text: 'Categoria creada correctamente.' })
      }

      setFormOpen(false)
      setFormCategory(emptyCategory)
      await loadData(pagination.page, { clearNotice: false })
    } catch (error) {
      setNotice({ type: 'error', text: error?.message || 'No fue posible guardar la categoria.' })
    } finally {
      setSaving(false)
    }
  }

  const confirmDeleteCategory = async () => {
    if (!deleteTarget) return

    setSaving(true)
    setNotice(null)
    try {
      await entrepreneurCatalogService.deleteCategory(deleteTarget.id)
      setNotice({ type: 'success', text: 'Categoria eliminada.' })
      setDeleteTarget(null)
      await loadData(pagination.page, { clearNotice: false })
    } catch (error) {
      setNotice({ type: 'error', text: error?.message || 'No fue posible eliminar la categoria.' })
    } finally {
      setSaving(false)
    }
  }

  const isBusy = loading || saving

  const columns = [
    {
      key: 'name',
      header: 'Nombre',
      render: (category) => (
        <div>
          <div className="font-semibold text-gray-900">{category.name}</div>
          <div className="mt-1 text-xs text-gray-500 line-clamp-2">
            {category.description || 'Sin descripcion registrada.'}
          </div>
        </div>
      ),
    },
    {
      key: 'slug',
      header: 'Slug',
      render: (category) => (
        <code className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-700">
          {category.slug}
        </code>
      ),
    },
    {
      key: 'sortOrder',
      header: 'Orden',
      render: (category) => (
        <span className="font-medium text-gray-700">
          {category.sortOrder ?? 0}
        </span>
      ),
      cellClassName: 'text-gray-700',
    },
    {
      key: 'isActive',
      header: 'Estado',
      render: (category) => (
        <Badge variant={category.isActive ? 'success' : 'warning'}>
          {category.isActive ? 'Activa' : 'Inactiva'}
        </Badge>
      ),
    },
  ]

  if (loading) {
    return (
      <PageContainer>
        <div className="flex justify-center py-10">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader
        title="Categorias"
        subtitle="Gestiona las categorias del catalogo con paginacion del servidor."
        actions={(
          <Button leftIcon={<Plus className="w-4 h-4" />} onClick={openCreateModal} disabled={!profile}>
            Nueva categoria
          </Button>
        )}
      />

      {notice && (
        <div
          className={`mb-6 rounded-lg px-4 py-3 text-sm ${
            notice.type === 'success'
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-700'
          }`}
        >
          {notice.text}
        </div>
      )}

      {!profile && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Primero configura el perfil del negocio en Perfil para poder crear categorias.
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderTree className="w-5 h-5 text-primary-600" />
            Categorias del catalogo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {categories.length === 0 ? (
            <EmptyState
              icon={FolderTree}
              title="Aun no hay categorias"
              description="Crea primero las categorias para clasificar mejor tus productos."
              size="sm"
              action={(
                <Button leftIcon={<Plus className="w-4 h-4" />} onClick={openCreateModal} disabled={!profile}>
                  Nueva categoria
                </Button>
              )}
            />
          ) : (
            <>
              <DataTable
                columns={columns}
                rows={categories}
                actions={(category) => (
                  <>
                    <Button
                      size="sm"
                      variant="secondary"
                      leftIcon={<Eye className="w-4 h-4" />}
                      onClick={() => setViewCategory(category)}
                      disabled={isBusy}
                    >
                      Ver
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<Pencil className="w-4 h-4" />}
                      onClick={() => openEditModal(category)}
                      disabled={isBusy}
                    >
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      leftIcon={<Trash2 className="w-4 h-4" />}
                      onClick={() => setDeleteTarget(category)}
                      disabled={isBusy}
                    >
                      Eliminar
                    </Button>
                  </>
                )}
                emptyState={null}
              />

              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalCount}
                pageSize={pagination.pageSize}
                onPageChange={(page) => loadData(page)}
              />
            </>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        size="lg"
        title={formMode === 'create' ? 'Nueva categoria' : 'Editar categoria'}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={submitCategory} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </>
        )}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Nombre"
            value={formCategory.name}
            onChange={(event) => setFormCategory((current) => ({ ...current, name: event.target.value }))}
            disabled={saving}
          />
          <Input
            label="Slug"
            value={formCategory.slug}
            onChange={(event) => setFormCategory((current) => ({ ...current, slug: event.target.value }))}
            disabled={saving}
          />
          <Input
            label="Orden"
            type="number"
            value={String(formCategory.sortOrder)}
            onChange={(event) => setFormCategory((current) => ({ ...current, sortOrder: Number(event.target.value) }))}
            disabled={saving}
          />
          <Select
            label="Estado"
            value={String(formCategory.isActive)}
            onChange={(event) => setFormCategory((current) => ({ ...current, isActive: event.target.value === 'true' }))}
            disabled={saving}
            options={[
              { value: 'true', label: 'Activa' },
              { value: 'false', label: 'Inactiva' },
            ]}
          />
          <div className="md:col-span-2">
            <Textarea
              label="Descripcion"
              rows={4}
              value={formCategory.description}
              onChange={(event) => setFormCategory((current) => ({ ...current, description: event.target.value }))}
              disabled={saving}
            />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(viewCategory)}
        onClose={() => setViewCategory(null)}
        size="lg"
        title="Ver categoria"
        footer={(
          <Button onClick={() => setViewCategory(null)}>
            Cerrar
          </Button>
        )}
      >
        {viewCategory && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-gray-500">Nombre</div>
                <div className="mt-1 text-gray-900">{viewCategory.name}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Slug</div>
                <div className="mt-1 text-gray-900">{viewCategory.slug}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Orden</div>
                <div className="mt-1 text-gray-900">{viewCategory.sortOrder ?? 0}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Estado</div>
                <div className="mt-1">
                  <Badge variant={viewCategory.isActive ? 'success' : 'warning'}>
                    {viewCategory.isActive ? 'Activa' : 'Inactiva'}
                  </Badge>
                </div>
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500">Descripcion</div>
              <div className="mt-1 whitespace-pre-wrap rounded-xl border border-gray-200 bg-gray-50 p-4 text-gray-700">
                {viewCategory.description || 'Sin descripcion registrada.'}
              </div>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDeleteCategory}
        title="Eliminar categoria"
        message={`Se eliminara la categoria "${deleteTarget?.name || ''}". Esta accion no se puede deshacer.`}
        confirmText="Eliminar"
        loading={saving}
      />
    </PageContainer>
  )
}
